/**
 * JOB HUNTER › CANDIDATES — /job-hunter/candidates
 *
 * Everyone the agents hunt for. A candidate is a person: truth file, CV
 * library, portfolio and WhatsApp alerts; each has its own agents. `?new=1`
 * opens the new-candidate form.
 */
import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowRight, Bot, BriefcaseBusiness, FileCheck, FileText, FileUp, GraduationCap, IdCard, Inbox, Languages, Link2, LoaderCircle, Mail, MapPin, Send, ShieldCheck, Sparkles, UserPlus, Wrench, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { JH, JobHunterShell } from '@/components/job-hunter/JobHunterShell';
import { CandidateAvatar, Field, Pill, ScoreRing } from '@/components/job-hunter/ui';
import { refreshJobHunter, setActiveProfile, upsertProfileInStore, useJobHunter } from '@/lib/job-hunter/store';
import { jobHunterService, type JhTruthFile } from '@/services/job-hunter.service';
import { syncSummary } from '@/components/job-hunter/candidate/ReadFromCv';

function NewCandidate({ onDone }: { onDone: () => void }) {
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [headline, setHeadline] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const pick = useRef<HTMLInputElement>(null);

  const choose = (f?: File) => {
    if (!f) return;
    if (!/\.(pdf|txt|md)$/i.test(f.name) || f.size > 8 * 1024 * 1024) { toast.error('Attach the CV as a PDF (or text file) up to 8 MB'); return; }
    setFile(f);
  };
  const create = async () => {
    const typed = name.trim();
    const fallbackName = file ? file.name.replace(/\.(pdf|txt|md)$/i, '').replace(/[_-]+/g, ' ').replace(/\b(cv|resume|résumé)\b/gi, '').replace(/\s+/g, ' ').trim() || 'New candidate' : '';
    setBusy('Creating the candidate…');
    try {
      const defaults = (await jobHunterService.meta()).defaults;
      const base = (defaults.truthFile ?? {}) as JhTruthFile;
      const p = await jobHunterService.createProfile({ name: typed || fallbackName, headline: headline.trim() || undefined, truthFile: { ...base, identity: { ...(base.identity ?? {}), name: typed } } });
      upsertProfileInStore(p);
      setActiveProfile(p.profileUid);
      if (file) {
        setBusy('Reading the CV into the profile…');
        try {
          // No name typed: the CV's name, headline and facts win. A typed name is kept; the CV fills the rest.
          const r = await jobHunterService.uploadCv(p.profileUid, file, { syncProfile: typed ? 'fill' : 'replace' });
          if (r.profileSync?.error) toast(r.profileSync.error, { icon: '⚠️', duration: 7000 });
          toast.success(r.profileSync?.changed.length ? `Candidate created and filled from the CV: ${syncSummary(r.profileSync)}` : 'Candidate created with the CV');
        } catch { toast.error('The candidate was created, but the CV upload failed — add it again on the CVs tab.'); }
      } else {
        toast.success('Candidate created with a first agent — add a CV next');
      }
      void refreshJobHunter();
      onDone();
      nav(JH.candidate(p.profileUid, file ? undefined : 'cvs'));
    } catch { /* toasted */ } finally { setBusy(null); }
  };
  return (
    <div className="@container">
      <div className="jh-card grid overflow-hidden @4xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,1fr)]">
        <section className="flex min-w-0 flex-col p-5 @2xl:p-7">
          <div className="jh-eyebrow">New candidate</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight">Who are the agents hunting for?</h1>
          <p className="jh-muted mt-1 max-w-2xl">Attach a CV and the profile fills itself. You can correct anything after.</p>

          <input ref={pick} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain" className="hidden" onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ''; }} />
          {file ? (
            <div className="mt-5 flex flex-1 items-center gap-3 rounded-2xl border border-success/40 bg-success-soft/40 px-5 py-6">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-success-soft text-success"><FileCheck size={22} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold">{file.name}</span>
                <span className="block text-[12px] text-text-muted">{fileSize(file.size)} · read when you create the candidate</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => pick.current?.click()} disabled={!!busy}>Change</Button>
              <button type="button" aria-label="Remove file" onClick={() => setFile(null)} disabled={!!busy} className="grid h-8 w-8 place-items-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"><X size={15} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => pick.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); choose(e.dataTransfer.files?.[0]); }}
              className={clsx('mt-5 flex min-h-[176px] w-full flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition',
                dragging ? 'border-primary bg-primary-soft/60' : 'border-border-strong hover:border-primary hover:bg-primary-soft/30')}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-soft text-primary"><FileUp size={22} /></span>
              <span className="text-[14px] font-semibold">Drop the CV here, or click to choose</span>
              <span className="text-[12px] text-text-muted">PDF or text file, up to 8 MB · recommended</span>
            </button>
          )}

          <div className="mt-5 grid gap-4 @2xl:grid-cols-2">
            <Field label="Candidate name" hint={file ? 'Leave empty to take it from the CV.' : undefined}><input className="jh-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={file ? 'From the CV' : 'e.g. Ibrahim Akram'} maxLength={120} /></Field>
            <Field label="Headline (optional)"><input className="jh-input" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder={file ? 'From the CV' : 'Senior Flutter & backend engineer, 5 yrs'} maxLength={200} /></Field>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-6">
            {busy && <span className="mr-auto inline-flex items-center gap-2 text-[12.5px] text-text-muted"><LoaderCircle size={14} className="animate-spin text-primary" />{busy}</span>}
            <Button variant="ghost" onClick={onDone} disabled={!!busy}>Cancel</Button>
            <Button loading={!!busy} disabled={!name.trim() && !file} iconLeft={<UserPlus size={15} />} onClick={create}>Create candidate</Button>
          </div>
        </section>

        <aside className="flex min-w-0 flex-col gap-6 border-t border-border bg-surface-alt/40 p-5 @2xl:p-7 @4xl:border-l @4xl:border-t-0">
          <div>
            <h3 className="jh-card-title"><Sparkles size={15} className="text-primary" />What the CV fills in</h3>
            <ul className="mt-3 grid grid-cols-2 gap-1.5">
              {CV_FILLS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-2 rounded-lg bg-surface px-2.5 py-2 text-[12.5px] shadow-[0_1px_2px_#0000000a]"><Icon size={14} className="shrink-0 text-primary" />{label}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="jh-card-title"><ShieldCheck size={15} className="text-success" />You answer these yourself</h3>
            <p className="jh-card-sub">A CV rarely states them, so the agents never guess: work rights and visa, availability and notice, salary, saved answers.</p>
          </div>
          <div>
            <h3 className="jh-card-title"><Bot size={15} className="text-primary" />Then</h3>
            <ol className="mt-2 grid gap-2 text-[12.5px] text-text-secondary">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex gap-2.5"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary-soft text-[11px] font-bold text-primary">{i + 1}</span><span className="pt-px">{s}</span></li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}

const CV_FILLS = [
  { icon: IdCard, label: 'Name and headline' },
  { icon: Mail, label: 'Email and phone' },
  { icon: MapPin, label: 'Location and nationality' },
  { icon: Languages, label: 'Languages' },
  { icon: GraduationCap, label: 'Education' },
  { icon: BriefcaseBusiness, label: 'Years of experience' },
  { icon: Wrench, label: 'Skills' },
  { icon: Link2, label: 'GitHub, LinkedIn, site' },
];

const NEXT_STEPS = [
  'A first agent, “Main search”, starts on the default settings.',
  'Check the profile and correct anything the CV got wrong.',
  'Add more CVs or agents for other roles and countries.',
];

const fileSize = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

/** The old single-candidate URL (/job-hunter/profile, #alerts, ?new=1) → the candidate pages. */
export function JhProfileRedirect() {
  const { profiles, profileUid } = useJobHunter();
  const [params] = useSearchParams();
  if (params.get('new') === '1' || (profiles && !profiles.length)) return <Navigate to={JH.NEW_CANDIDATE} replace />;
  if (!profiles || !profileUid) return null;
  const hash = window.location.hash.slice(1);
  const tab = hash === 'alerts' ? 'alerts' : hash === 'cv' ? 'cvs' : hash === 'portfolio' ? 'portfolio' : hash === 'scoring' ? 'limits' : undefined;
  return <Navigate to={JH.candidate(profileUid, tab)} replace />;
}

export default function JhCandidates() {
  const { profiles, profileUid, setActive } = useJobHunter();
  const [params, setParams] = useSearchParams();
  const creating = params.get('new') === '1';

  if (creating || (profiles !== null && profiles.length === 0)) {
    return (
      <JobHunterShell section="Candidates" description="The people the agents hunt for" needsProfile={false}>
        <NewCandidate onDone={() => setParams({})} />
      </JobHunterShell>
    );
  }

  return (
    <JobHunterShell section="Candidates" description="The people the agents hunt for"
      actions={<Button size="sm" iconLeft={<UserPlus size={14} />} onClick={() => setParams({ new: '1' })}>New candidate</Button>}>
      <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {(profiles ?? []).map((p) => {
          const c = p.counts;
          return (
            <Link key={p.profileUid} to={JH.candidate(p.profileUid)} onClick={() => setActive(p.profileUid)}
              className="jh-card group flex flex-col gap-4 p-4 transition hover:border-primary">
              <div className="flex items-start gap-3">
                <CandidateAvatar name={p.name} photoUrl={p.photo?.url} size={48} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-[15px] font-bold tracking-tight">{p.name}</h3>
                    {p.profileUid === profileUid && <Pill tone="primary">In view</Pill>}
                    {!p.active && <Pill tone="neutral">Paused</Pill>}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-[12.5px] text-text-secondary">{p.headline || 'No headline yet'}</p>
                </div>
                <ScoreRing score={c?.bestCvScore ?? null} size={44} stroke={4} />
              </div>
              {/* Cards sit two-up beside the sidebar: wrap the chips instead of forcing four columns. */}
              <div className="flex flex-wrap gap-2 text-[12px]">
                <span className="flex items-center gap-1.5 rounded-lg bg-surface-alt/70 px-2 py-1.5"><FileText size={13} className="text-text-muted" /><b className="tabular-nums">{c?.cvs ?? 0}</b> CV{c?.cvs === 1 ? '' : 's'}</span>
                <span className="flex items-center gap-1.5 rounded-lg bg-surface-alt/70 px-2 py-1.5"><Bot size={13} className="text-text-muted" /><b className="tabular-nums">{c?.activeAgents ?? 0}/{c?.agents ?? 0}</b> agents</span>
                <span className="flex items-center gap-1.5 rounded-lg bg-surface-alt/70 px-2 py-1.5"><Inbox size={13} className="text-text-muted" /><b className="tabular-nums">{c?.openMatches ?? 0}</b> open</span>
                <span className="flex items-center gap-1.5 rounded-lg bg-surface-alt/70 px-2 py-1.5"><Send size={13} className="text-text-muted" /><b className="tabular-nums">{c?.applied ?? 0}</b> applied</span>
              </div>
              <div className="flex items-center justify-between text-[12px] text-text-muted">
                <span>{p.notifyEnabled && p.notifyPhone ? `WhatsApp alerts to +${p.notifyPhone}` : 'WhatsApp alerts off'}</span>
                <span className="inline-flex items-center gap-1 font-semibold text-primary opacity-0 transition group-hover:opacity-100">Open <ArrowRight size={13} /></span>
              </div>
            </Link>
          );
        })}
        <button type="button" onClick={() => setParams({ new: '1' })} className="grid min-h-[180px] place-items-center rounded-2xl border-2 border-dashed border-border text-text-muted transition hover:border-primary hover:text-primary">
          <span className="flex flex-col items-center gap-2 text-[13px] font-semibold"><UserPlus size={22} />New candidate</span>
        </button>
      </div>
    </JobHunterShell>
  );
}
